import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { type FC, type ReactNode } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Card, Display, Text, cn, useRebrand } from '@growtherapy/sprout-ui';

import PreSessionImage from '../../../assets/images/pre-session-image.svg?react';
import { useAdmissionFlow, useIsAutomaticAdmissionEnabled } from '../../../hooks/useAdmissionFlow';
import { useVideoPlatform } from '../../../hooks/useVideoPlatform';
import { telehealthAutoOpenIncompleteMeasuresDrawer } from '../../../statsig/gates';
import { useGetClientSafetyGuard } from '../../../useGetClientSafetyGuard';
import { VideoPlatform } from '../../../video-sdk';
import { videoRoomTokenAtom } from '../../../video-sdk/core/room/state';
import { VisitorDrawerState, visitorMeasuresAtom } from '../../state';
import { WaitingRoomState } from '../types';
import { ClientMeasuresCta } from './ClientMeasuresCta';
import { JoinSessionCta } from './JoinSessionCta';
import { ReflectionTopicsCtaWrapper } from './ReflectionTopicsCta';
import { VibesCtaWrapper } from './VibesCta';

export interface PreSessionPromptProps {
  callToActionContent: ReactNode;
  hasOutstandingMeasures: boolean;
  isAutoOpenMeasuresDrawerEnabled: boolean;
  isZoomManualFlow?: boolean;
  state?: WaitingRoomState;
}

export type CTAProps = {
  openDrawer: (drawer: VisitorDrawerState) => void;
  state?: WaitingRoomState;
  variant?: PreSessionPromptVariant;
};

export enum PreSessionPromptVariant {
  CLIENT_MEASURES = 'CLIENT_MEASURES',
  REFLECTION_TOPICS = 'REFLECTION_TOPICS',
  VIBES = 'VIBES',
}

export const VariantComponents: Record<PreSessionPromptVariant, FC<CTAProps>> = {
  [PreSessionPromptVariant.CLIENT_MEASURES]: ClientMeasuresCta,
  [PreSessionPromptVariant.VIBES]: VibesCtaWrapper,
  [PreSessionPromptVariant.REFLECTION_TOPICS]: ReflectionTopicsCtaWrapper,
};

function BodyContent({
  isAutoOpenMeasuresDrawerEnabled,
  hasOutstandingMeasures,
  isZoomManualFlow,
  state,
}: {
  isAutoOpenMeasuresDrawerEnabled: boolean;
  hasOutstandingMeasures: boolean;
  isZoomManualFlow?: boolean;
  state?: WaitingRoomState;
}) {
  const t = useTranslate();
  if (state === WaitingRoomState.ADMITTED) {
    if (hasOutstandingMeasures && isAutoOpenMeasuresDrawerEnabled) {
      return (
        <span>
          {t('telehealth-core:waitingRoom.yourClinicianIsReadyToBegin')}{' '}
          {t('telehealth-core:waitingRoom.youCanFinishYourHealthCheckInFormsNowOrAfterTheSessionStarts')}
        </span>
      );
    }
    return t('telehealth-core:waitingRoom.yourClinicianIsReadyToBegin');
  }
  const sessionWillStartSoonText = isZoomManualFlow
    ? t('telehealth-core:waitingRoom.yourProviderWillInviteYouIn')
    : t('telehealth-core:waitingRoom.yourProviderWillStartYourSessionSoon');
  const whenYouCanJoinText = isZoomManualFlow
    ? t('telehealth-core:waitingRoom.yourProviderWillInviteYouIn')
    : t('telehealth-core:waitingRoom.youCanJoinWhenYourProviderIsReady');
  if (isAutoOpenMeasuresDrawerEnabled) {
    if (hasOutstandingMeasures) {
      return (
        <span>
          {sessionWillStartSoonText}{' '}
          {t('telehealth-core:waitingRoom.youCanFinishYourHealthCheckInFormsNowOrAfterTheSessionStarts')}
        </span>
      );
    }
    return sessionWillStartSoonText;
  }
  return (
    <span>
      {whenYouCanJoinText}
      <br />
      {t('telehealth-core:waitingRoom.theyHave10MinGracePeriod')}
    </span>
  );
}

export function PreSessionPrompt({
  callToActionContent,
  hasOutstandingMeasures,
  isAutoOpenMeasuresDrawerEnabled,
  isZoomManualFlow,
  state,
}: PreSessionPromptProps) {
  const t = useTranslate();
  const isRebrand = useRebrand();
  const { headingContent, bodyContent } = {
    headingContent:
      state === WaitingRoomState.ADMITTED
        ? t('telehealth-core:waitingRoom.letsStartYourSession')
        : t('telehealth-core:waitingRoom.youreInTheRightPlace'),
    bodyContent: (
      <BodyContent
        isAutoOpenMeasuresDrawerEnabled={isAutoOpenMeasuresDrawerEnabled}
        hasOutstandingMeasures={hasOutstandingMeasures}
        isZoomManualFlow={isZoomManualFlow}
        state={state}
      />
    ),
  };

  return (
    <Card className='flex flex-col mobile:border-x rounded-xl items-center max-w-[38rem] gap-y-10 sm:p-10 rebrand:border-border-default rebrand:bg-surface-inverse'>
      {!isRebrand && (
        <div className='hidden sm:block'>
          <PreSessionImage
            aria-label={t('telehealth-core:waitingRoom.aLightPurpleSketchOfAPairOfHandsClaspedTogether')}
          />
        </div>
      )}

      <div className='flex flex-col gap-y-4 items-center'>
        {isRebrand ? (
          <Display.Small className='rebrand:text-text-inverse'>{headingContent}</Display.Small>
        ) : (
          <Text as='h2' variant='lg' className='font-medium'>
            {headingContent}
          </Text>
        )}

        <Body.Default
          className={cn('text-neutral-700 text-center rebrand:text-text-inverse', {
            'max-w-[30rem]': isAutoOpenMeasuresDrawerEnabled,
          })}
        >
          {bodyContent}
        </Body.Default>

        {callToActionContent}
      </div>
    </Card>
  );
}

export function PreSessionPromptWrapper({
  isLoading,
  variant,
  openDrawer,
}: {
  isLoading: boolean;
  variant?: PreSessionPromptVariant;
  openDrawer: (drawer: VisitorDrawerState) => void;
}) {
  const admissionFlow = useAdmissionFlow();
  const hasRoomToken = !!useAtomValue(videoRoomTokenAtom);
  // TODO(telehealth_automatic_admission_xp): remove if the Test group ships
  const isAdmitted = admissionFlow === 'manual' && hasRoomToken;
  const { value: isAutoOpenMeasuresDrawerEnabled } = useFeatureGate(telehealthAutoOpenIncompleteMeasuresDrawer);
  const isAutomaticAdmissionEnabled = useIsAutomaticAdmissionEnabled();
  const isZoomManualFlow = useVideoPlatform() === VideoPlatform.Zoom && !isAutomaticAdmissionEnabled;
  const visitorMeasures = useAtomValue(visitorMeasuresAtom);
  const { requiresSafetyGuards } = useGetClientSafetyGuard();
  const showOutstandingMeasures = !!visitorMeasures?.length && requiresSafetyGuards === false;

  let CallToAction: FC<CTAProps> | null = null;
  if (isAdmitted) {
    CallToAction = JoinSessionCta;
    // We suppress all CTAs except for the join session CTA when the client has
    // outstanding measures and the auto open measures drawer feature gate is
    // enabled to encourage measure completion.
  } else if (isAutoOpenMeasuresDrawerEnabled && showOutstandingMeasures) {
    CallToAction = null;
  } else if (isLoading) {
    // We also suppress the CTA while the page is loading, in case new data
    // coming in changes the decision about which CTA to show.
    CallToAction = null;
  } else if (variant) {
    CallToAction = VariantComponents[variant];
  }

  const state = isAdmitted ? WaitingRoomState.ADMITTED : WaitingRoomState.AWAITING_ADMISSION;

  return (
    <PreSessionPrompt
      callToActionContent={
        CallToAction ? <CallToAction openDrawer={openDrawer} state={state} variant={variant} /> : null
      }
      hasOutstandingMeasures={showOutstandingMeasures}
      isAutoOpenMeasuresDrawerEnabled={isAutoOpenMeasuresDrawerEnabled}
      isZoomManualFlow={isZoomManualFlow}
      state={state}
    />
  );
}
